import { Injectable } from '@angular/core';
import { WsACKCallback, WsACKResponse } from '@common/api/ack';
import { RealtimeFeatureCommandPayload } from '@common/api/realtime';
import { AuthQuery } from '@ep-om/project/auth/auth.query';
import { shouldUseRealtimeV2 } from './realtime-v2-feature';
import { RealtimeV2CommandFailure, RealtimeV2CommandOptions } from './realtime-v2.models';
import { RealtimeV2SocketService } from './realtime-v2-socket.service';

@Injectable({
  providedIn: 'root',
})
export class RealtimeFeatureCommandService {
  constructor(
    private realtimeV2Socket: RealtimeV2SocketService,
    private authQuery: AuthQuery,
  ) {}

  send<TBody>(
    payload: RealtimeFeatureCommandPayload,
    legacySend: () => void,
    callback?: WsACKCallback<TBody>,
    options?: RealtimeV2CommandOptions,
  ): void {
    if (!this.isRealtimeV2Enabled()) {
      legacySend();
      return;
    }

    this.sendRealtimeV2(payload, callback, options);
  }

  private async sendRealtimeV2<TBody>(
    payload: RealtimeFeatureCommandPayload,
    callback?: WsACKCallback<TBody>,
    options?: RealtimeV2CommandOptions,
  ): Promise<void> {
    try {
      const result = await this.realtimeV2Socket.featureCommand(payload, {
        ...options,
        expectsCompletion: true,
      });
      callback?.(this.toWsAckResponse(result.body));
    } catch (error) {
      const response = this.toWsAckError(error);
      if (callback) {
        callback(response);
        return;
      }
      console.error('[REALTIME-V2] feature command failed', response);
    }
  }

  private toWsAckResponse<TBody>(body: any): WsACKResponse<TBody> {
    if (body?.status === 'success' || body?.status === 'error') {
      return body;
    }

    return {
      status: 'success',
      ...(body !== undefined && { body }),
    };
  }

  private toWsAckError(error: RealtimeV2CommandFailure | any): WsACKResponse<any> {
    return {
      status: 'error',
      message: error?.message || 'Realtime V2 feature command failed',
      exception: error?.code,
      ...(error?.details && { body: error.details }),
    };
  }

  private isRealtimeV2Enabled(): boolean {
    return shouldUseRealtimeV2(this.authQuery.getValue().user);
  }
}
